import React, { useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles } from '@/utils/pageBranding'
import { AdminDocumentList, AdminDocumentStats } from '@/components/documents'
import { Button } from '@/components/ui/button'
import { BarChart3, Clock, FileText, RefreshCw } from 'lucide-react'

export const AdminDocuments: React.FC = () => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const [activeTab, setActiveTab] = useState<'stats' | 'pending' | 'all'>('stats')
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        <div className="mb-8">
          <p className="text-lg" style={pageStyles.textSecondary}>
            Review and manage user verification documents for KYC compliance.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-3 px-4 border-b-2 font-semibold text-sm transition-all duration-300 flex items-center space-x-2 ${activeTab === 'stats'
                ? 'border-transparent'
                : 'border-transparent hover:opacity-80'
                }`}
              style={{
                borderBottomColor: activeTab === 'stats' ? branding.primaryColor : 'transparent',
                color: activeTab === 'stats' ? branding.primaryColor : branding.textSecondaryColor
              }}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Statistics</span>
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-3 px-4 border-b-2 font-semibold text-sm transition-all duration-300 flex items-center space-x-2 ${activeTab === 'pending'
                ? 'border-transparent'
                : 'border-transparent hover:opacity-80'
                }`}
              style={{
                borderBottomColor: activeTab === 'pending' ? branding.primaryColor : 'transparent',
                color: activeTab === 'pending' ? branding.primaryColor : branding.textSecondaryColor
              }}
            >
              <Clock className="w-4 h-4" />
              <span>Pending Review</span>
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-3 px-4 border-b-2 font-semibold text-sm transition-all duration-300 flex items-center space-x-2 ${activeTab === 'all'
                ? 'border-transparent'
                : 'border-transparent hover:opacity-80'
                }`}
              style={{
                borderBottomColor: activeTab === 'all' ? branding.primaryColor : 'transparent',
                color: activeTab === 'all' ? branding.primaryColor : branding.textSecondaryColor
              }}
            >
              <FileText className="w-4 h-4" />
              <span>All Documents</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'stats' && <AdminDocumentStats />}

          {activeTab === 'pending' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold" style={pageStyles.text}>Pending Documents</h2>
                <Button
                  onClick={handleRefresh}
                  size="sm"
                  variant="outline"
                  className="hover:opacity-80"
                  style={{ backgroundColor: `${branding.primaryColor}20`, borderColor: `${branding.primaryColor}40`, color: branding.primaryColor }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <AdminDocumentList showPendingOnly={true} key={refreshTrigger} />
            </div>
          )}

          {activeTab === 'all' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold" style={pageStyles.text}>All Documents</h2>
                <Button
                  onClick={handleRefresh}
                  size="sm"
                  variant="outline"
                  className="hover:opacity-80"
                  style={{ backgroundColor: `${branding.primaryColor}20`, borderColor: `${branding.primaryColor}40`, color: branding.primaryColor }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <AdminDocumentList showPendingOnly={false} key={refreshTrigger} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
